import { useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import { QUESTIONNAIRE_STATUS } from '../../../../../../constants';
import { DOCUMENT_TYPES } from '../../../../../../pages/questionnaires/onboarding/constants';
import { onboardingQuestionnaireActions } from '../../../../../../store';
import {
  COMPLETED_QUESTIONNAIRE_DOCUMENT_MUTATION_MESSAGE,
  COMPLETED_QUESTIONNAIRE_DOCUMENT_TOAST_ID,
} from '../constants';

import type { AppDispatch } from '../../../../../../store';
import type { IOnboardingQuestionnaireDocument } from '../../../../../../pages/questionnaires/onboarding/types';
import type { UploadOnboardingQuestionnaireDocumentParams } from './types';

export function useUploadOnboardingQuestionnaireDocument() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const [uploadDocumentLoading, setUploadDocumentLoading] = useState(false);

  const uploadDocument = async ({
    onboardingQuestionnaireId,
    file,
    title,
    documentType,
    documentSubType,
    existingDocuments,
    questionnaireStatus,
  }: UploadOnboardingQuestionnaireDocumentParams): Promise<
    IOnboardingQuestionnaireDocument[] | null
  > => {
    if (questionnaireStatus === QUESTIONNAIRE_STATUS.COMPLETED) {
      toast.error(COMPLETED_QUESTIONNAIRE_DOCUMENT_MUTATION_MESSAGE, {
        toastId: COMPLETED_QUESTIONNAIRE_DOCUMENT_TOAST_ID,
      });
      return null;
    }

    setUploadDocumentLoading(true);
    try {
      // INSURANCE_CARD allows one document per subtype; replace before upload (matches client form).
      if (documentType === DOCUMENT_TYPES.INSURANCE_CARD && documentSubType) {
        const documentsToReplace = existingDocuments.filter(
          (document) => document.document_sub_type === documentSubType,
        );

        for (const document of documentsToReplace) {
          const deleteResult = await dispatch(
            onboardingQuestionnaireActions.deleteOnboardingQuestionnaireDocument({
              onboardingQuestionnaireId,
              documentId: String(document.id),
              getAccessTokenSilently,
            }),
          );

          if (
            !onboardingQuestionnaireActions.deleteOnboardingQuestionnaireDocument.fulfilled.match(
              deleteResult,
            )
          ) {
            return null;
          }
        }
      }

      const result = await dispatch(
        onboardingQuestionnaireActions.uploadOnboardingQuestionnaireDocument({
          onboardingQuestionnaireId,
          data: {
            file,
            title,
            document_type: documentType,
            document_sub_type: documentSubType,
          },
          getAccessTokenSilently,
        }),
      );

      if (
        onboardingQuestionnaireActions.uploadOnboardingQuestionnaireDocument.fulfilled.match(result)
      ) {
        toast.success('Document uploaded successfully');
        return result.payload;
      }

      return null;
    } finally {
      setUploadDocumentLoading(false);
    }
  };

  return {
    uploadDocument,
    uploadDocumentLoading,
  };
}
